import { useState, useEffect, useRef } from "react";
import vdo from '../../../Assets/Home/video2.mp4';
import { useNavigate } from "react-router-dom";

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

const steps = [
  {
    title: "Consultation & Planning",
    description:
      "We begin with understanding your needs, goals, and budget to develop a clear and strategic construction plan.",
    active: true,
  },
  {
    title: "Design & Proposal",
    description:
      "Our architects and engineers create detailed designs and cost estimates that align with your project's vision and requirements.",
    active: false,
  },
  {
    title: "Construction Execution",
    description:
      "Skilled professionals manage the entire build process, maintaining high standards, safety, and efficiency from start to finish.",
    active: false,
  },
  {
    title: "Final Delivery",
    description:
      "We hand over your completed project with thorough inspection, quality assurance, and post-completion support for lasting satisfaction.",
    active: false,
  },
];

function StepItem({ step, index, visible }) {
  const [hovered, setHovered] = useState(false);
  const isFirst = index === 0;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex gap-5 group cursor-pointer"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(40px)",
        transition: `opacity 0.6s ease ${0.1 + index * 0.12}s, transform 0.6s cubic-bezier(0.34,1.2,0.64,1) ${0.1 + index * 0.12}s`,
      }}
    >
      {/* Dot + line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className="w-5 h-5 rounded-full border-[3px] flex-shrink-0 transition-all duration-300 mt-1"
          style={{
            borderColor: isFirst || hovered ? "#08a615" : "#0f1f4b",
            background: isFirst || hovered ? "#08a615" : "transparent",
            boxShadow: isFirst || hovered ? "0 0 0 4px #54eb61" : "none",
            transform: hovered ? "scale(1.2)" : "scale(1)",
          }}
        />
        {index < steps.length - 1 && (
          <div
            className="w-px flex-1 mt-2 transition-all duration-500"
            style={{
              background: hovered
                ? "linear-gradient(to bottom, #08a615, rgba(15,31,75,0.2))"
                : "rgba(15,31,75,0.15)",
              minHeight: "48px",
            }}
          />
        )}
      </div>

      {/* Content */}
      <div className="pb-8">
        <h4
          className="font-black uppercase mb-2 transition-colors duration-300"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: "clamp(15px, 1.4vw, 19px)",
            letterSpacing: "0.06em",
            color: isFirst || hovered ? "#08a615" : "#0f1f4b",
          }}
        >
          {step.title}
        </h4>
        <p
          className="text-gray-500 text-sm leading-relaxed"
          style={{ fontFamily: "'DM Sans', sans-serif", maxWidth: "340px" }}
        >
          {step.description}
        </p>
      </div>
    </div>
  );
}

export default function HowWeWork() {
  const [leftRef, leftVisible] = useInView(0.1);
  const [imgRef, imgVisible] = useInView(0.1);
  const [stepsRef, stepsVisible] = useInView(0.1);
  const navigate = useNavigate();
  
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;700;800;900&family=DM+Sans:wght@400;500&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <div
      className=" flex items-center justify-center py-20 px-6 md:px-10 lg:px-12 xl:px-20 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #f0f4f8 0%, #e8edf5 50%, #f0f4f8 100%)",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          opacity: 0.3,
        }}
      />

      {/* Ambient glow */}
      <div
        className="absolute bottom-0 right-1/4 w-[500px] h-[400px] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse, rgba(234,88,12,0.05) 0%, transparent 70%)",
        }}
      />

      <div className="relative w-full ">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12 items-center">

          {/* LEFT — label + heading + CTA */}
          <div
            ref={leftRef}
            style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? "translateX(0)" : "translateX(-40px)",
              transition: "opacity 0.8s ease, transform 0.8s cubic-bezier(0.34,1.2,0.64,1)",
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-6 bg-green-400" />
              <span
                className="text-green-500 font-bold text-sm font-rajdhani tracking-wider uppercase"
              >
                How We Works
              </span>
            </div>

            <h2
              className="font-black uppercase text-gray-900 leading-[2.5] font-rajdhani text-2xl md:text-4xl lg:text-[45px] mb-10"
            >
              Four{" "}
              <span
              className="mx-2"
                style={{
                  WebkitTextStroke: "2px #0f1f4b",
                  color: "transparent",
                }}
              >
                Simple
              </span>
              Steps
              <br />
               Build
              Your Vision
            </h2>

            <button
            onClick={()=>{
              navigate("/contact");
              window.scrollTo({
                top: 0,
                behavior : "smooth"
              })
            }}
              className="group relative overflow-hidden font-rajdhani  flex items-center gap-3 bg-green-500 text-white font-bold uppercase text-sm md:text-base px-7 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-green-500/30"
            >
              <span className="relative z-10">Free Consultation</span>
              <span className="relative z-10 w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
              <div
                className="absolute inset-0 bg-gray-900 transition-transform duration-500 origin-left scale-x-0 group-hover:scale-x-100"
                style={{ zIndex: 0 }}
              />
            </button>
          </div>

          {/* CENTER — Image */}
          <div
            ref={imgRef}
            className="relative rounded-2xl overflow-hidden"
            style={{
              height: "clamp(340px, 48vw, 520px)",
              opacity: imgVisible ? 1 : 0,
              transform: imgVisible ? "translateY(0) scale(1)" : "translateY(30px) scale(0.97)",
              transition: "opacity 0.8s ease 0.2s, transform 0.8s cubic-bezier(0.34,1.2,0.64,1) 0.2s",
              boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
            }}
          >
           
            <video className="w-full h-full object-cover cursor-pointer" src={vdo} controls autoPlay muted/>
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(to top, rgba(10,20,50,0.35) 0%, transparent 55%)",
              }}
            />

            {/* Step count badge */}
            <div
              className="absolute top-5 left-5 px-4 py-2 rounded-xl"
              style={{
                background: "rgba(10,20,50,0.8)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <p
                className="text-white font-black text-lg leading-none"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                04
              </p>
              <p className="text-blue-200/60 text-[10px] uppercase tracking-widest mt-0.5">
                Easy Steps
              </p>
            </div>

            {/* green corner */}
            <div
              className="absolute bottom-0 right-0 w-14 h-14 rounded-tl-2xl"
              style={{ background: "linear-gradient(135deg, #42f551, #08a615)", opacity: 0.9 }}
            />
          </div>

          {/* RIGHT — Steps timeline */}
          <div ref={stepsRef} className="flex flex-col">
            {steps.map((step, i) => (
              <StepItem key={i} step={step} index={i} visible={stepsVisible} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}